<div class="schedules-page">
  <h1 class="page-title">Schedules</h1>
  <p class="page-sub">
    Working days are <strong>Monday–Saturday</strong>. <strong>Sunday</strong> and the <strong>second Saturday</strong> of each month are treated as holidays for scheduling. Period times, weekly timetable per class, and exams / tests.
  </p>

  <mat-tab-group animationDuration="0ms" class="schedule-tabs">
    <mat-tab label="Period times">
      <div class="tab-body">
        <p class="tab-help" *ngIf="!isAdmin">View only. Admins can edit bell schedule.</p>
        <p class="tab-help" *ngIf="isAdmin">Edit labels and start/end times, then save. Applies to all class grids.</p>
        <div *ngIf="loading" class="loading"><mat-spinner diameter="36"></mat-spinner></div>
        <table class="periods-table" *ngIf="!loading && periods.length">
          <thead>
            <tr>
              <th>#</th>
              <th>Label</th>
              <th>Start</th>
              <th>End</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let p of periods; let i = index">
              <td>{{ i + 1 }}</td>
              <td>
                <input class="cell-input" [(ngModel)]="p.label" [readonly]="!isAdmin" />
              </td>
              <td>
                <input class="cell-input time" type="time" [(ngModel)]="p.startTime" [readonly]="!isAdmin" />
              </td>
              <td>
                <input class="cell-input time" type="time" [(ngModel)]="p.endTime" [readonly]="!isAdmin" />
              </td>
            </tr>
          </tbody>
        </table>
        <div class="tab-actions" *ngIf="isAdmin">
          <button mat-flat-button color="primary" type="button" (click)="savePeriods()" [disabled]="savingPeriods">
            {{ savingPeriods ? 'Saving…' : 'Save period times' }}
          </button>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Class timetable">
      <div class="tab-body">
        <p class="tab-help schedule-policy">
          Timetable grid covers Mon–Sat. Sundays and second Saturdays are non-instruction days (see dashboard calendar for colour cues).
        </p>
        <div class="toolbar-row">
          <mat-form-field appearance="outline">
            <mat-label>Class</mat-label>
            <mat-select [(ngModel)]="selectedClass" (selectionChange)="onClassChange()">
              <mat-option *ngFor="let c of classOptions" [value]="c">{{ c }}</mat-option>
            </mat-select>
          </mat-form-field>
          <button *ngIf="isAdmin" mat-flat-button color="primary" type="button" (click)="saveTimetable()" [disabled]="!grid || savingGrid">
            {{ savingGrid ? 'Saving…' : 'Save timetable' }}
          </button>
        </div>
        <p class="tab-help" *ngIf="!isAdmin">You can view subjects per period; only admins can edit.</p>
        <div *ngIf="loading" class="loading"><mat-spinner diameter="36"></mat-spinner></div>
        <div class="scroll-wrap" *ngIf="!loading && grid">
          <table class="tt-grid">
            <thead>
              <tr>
                <th class="corner">Period</th>
                <th *ngFor="let d of days">{{ dayLabel(d) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of periods; let pi = index">
                <th class="period-head">
                  <div class="plabel">{{ p.label }}</div>
                  <div class="ptime">{{ p.startTime }}–{{ p.endTime }}</div>
                </th>
                <td *ngFor="let d of days">
                  <input
                    class="cell-input subject"
                    [(ngModel)]="grid.subjects[d][pi]"
                    [readonly]="!isAdmin"
                    placeholder="Subject"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Exams & tests">
      <div class="tab-body">
        <p class="tab-help">Schedule formal exams and class tests. Stored locally if the server has no API yet.</p>
        <form [formGroup]="examForm" class="exam-form" *ngIf="isAdmin">
          <mat-form-field appearance="outline">
            <mat-label>Title</mat-label>
            <input matInput formControlName="title" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Class</mat-label>
            <mat-select formControlName="className">
              <mat-option *ngFor="let c of classOptions" [value]="c">{{ c }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Date</mat-label>
            <input matInput [matDatepicker]="examPicker" formControlName="date" />
            <mat-datepicker-toggle matSuffix [for]="examPicker"></mat-datepicker-toggle>
            <mat-datepicker #examPicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Start</mat-label>
            <input matInput type="time" formControlName="startTime" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>End</mat-label>
            <input matInput type="time" formControlName="endTime" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Type</mat-label>
            <mat-select formControlName="type">
              <mat-option value="EXAM">Exam</mat-option>
              <mat-option value="TEST">Test</mat-option>
              <mat-option value="OTHER">Other</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button color="primary" type="button" (click)="addExam()" [disabled]="examForm.invalid">
            Add
          </button>
        </form>

        <table mat-table [dataSource]="examData" class="exam-table">
          <ng-container matColumnDef="title">
            <th mat-header-cell *matHeaderCellDef>Title</th>
            <td mat-cell *matCellDef="let row">{{ row.title }}</td>
          </ng-container>
          <ng-container matColumnDef="className">
            <th mat-header-cell *matHeaderCellDef>Class</th>
            <td mat-cell *matCellDef="let row">{{ row.className }}</td>
          </ng-container>
          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef>Date</th>
            <td mat-cell *matCellDef="let row">{{ row.date }}</td>
          </ng-container>
          <ng-container matColumnDef="time">
            <th mat-header-cell *matHeaderCellDef>Time</th>
            <td mat-cell *matCellDef="let row">{{ row.startTime }} – {{ row.endTime }}</td>
          </ng-container>
          <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef>Type</th>
            <td mat-cell *matCellDef="let row">{{ row.type }}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row">
              <button *ngIf="isAdmin" mat-icon-button type="button" color="warn" (click)="deleteExam(row)" aria-label="Delete">
                <mat-icon>delete</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="examColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: examColumns"></tr>
        </table>
        <p *ngIf="!examData.data.length" class="empty">No exams or tests yet.</p>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
